import React, { useRef, useMemo, useEffect, useState, useCallback } from 'react'
import {
  Box,
  Card,
  CardHeader,
  CardBody,
  Heading,
  HStack,
  VStack,
  Text,
  useDisclosure,
  Input,
  InputGroup,
  InputRightElement,
  IconButton,
  Badge,
  Tabs,
  TabList,
  Tab,
  Button,
} from '@chakra-ui/react'
import { AdminLayout } from '@/components/layout/AdminLayout'
import { DataTable, type DataTableRef, type DataTableOptions } from '@/components/tables'
import { ActionButtons } from '@/components/common/ActionButtons'
import { CreateTicketModal } from '@/components/client/CreateTicketModal'
import { AliceCallOutConfirmModal } from '@/components/client/AliceCallOutConfirmModal'
import { TicketRequestDetailModal } from '@/components/client/TicketRequestDetailModal'
import { useToastNotification } from '@/lib/utils/useToastNotification'
import { useAccessCheck } from '@/hooks/useAccess'
import { PhoneCall, Search } from 'lucide-react'
import { format, parseISO } from 'date-fns'
import { useThemeColors } from '@/hooks/useThemeColors'
import { useAuthStore } from '@/store/auth'
import type { VoiceAiCallPayload } from '@/lib/voiceAi/buildVoiceAiCallPayload'
import {
  buildCallOutPayloadFromRow,
  getProfileContactEmail,
  getProfileContactName,
  getProfileContactPhone,
  hasValidProfilePhone,
  notifyMissingProfilePhone,
  type ProfileUser,
} from '@/lib/voiceAi/profilePhone'
import { getTicketRequest } from '@/lib/services/ticketRequestService'
import type { TicketInitialData, TicketRequest } from '@/types/ticketRequest'
import { ticketRequestToInitialData } from '@/types/ticketRequest'

export default function MyRequestsPage() {
  const tableRef = useRef<DataTableRef>(null)
  const searchRef = useRef<HTMLInputElement>(null)
  const { isOpen, onOpen, onClose } = useDisclosure()
  const { showError } = useToastNotification()
  const access = useAccessCheck('/my-request')
  const colors = useThemeColors()
  const user = useAuthStore((state) => state.user as ProfileUser | null)

  const [searchQuery, setSearchQuery] = useState('')
  const [totalRequests, setTotalRequests] = useState(0)
  const [activeTab, setActiveTab] = useState<'all' | 'trash'>('all')
  const [totalTrash, setTotalTrash] = useState(0)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [duplicateFrom, setDuplicateFrom] = useState<TicketInitialData | null>(null)
  const [viewRowId, setViewRowId] = useState<number | null>(null)
  const [viewRow, setViewRow] = useState<TicketRequest | null>(null)
  const [isLoadingViewRow, setIsLoadingViewRow] = useState(false)
  const [callOutPayload, setCallOutPayload] = useState<VoiceAiCallPayload | null>(null)
  const {
    isOpen: isCallOutOpen,
    onOpen: onCallOutOpen,
    onClose: onCallOutClose,
  } = useDisclosure()

  const defaultContact = useMemo(
    () => ({
      name: getProfileContactName(user),
      email: getProfileContactEmail(user),
      phone: getProfileContactPhone(user),
    }),
    [user]
  )

  useEffect(() => {
    if (viewRowId == null) {
      setViewRow(null)
      setIsLoadingViewRow(false)
      return
    }

    let cancelled = false
    setViewRow(null)
    setIsLoadingViewRow(true)

    getTicketRequest(viewRowId)
      .then((data) => {
        if (cancelled) return
        setViewRow(data)
      })
      .catch(() => {
        if (cancelled) return
        setViewRow(null)
      })
      .finally(() => {
        if (!cancelled) setIsLoadingViewRow(false)
      })

    return () => {
      cancelled = true
    }
  }, [viewRowId])

  const showSubSubCallback = useMemo(
    () => (all: number, trashed: number) => {
      setTotalRequests(all)
      setTotalTrash(trashed)
    },
    []
  )

  const tableParams = useMemo(() => ({ search: searchQuery }), [searchQuery])

  const handleTabChange = (index: number) => {
    setActiveTab(index === 0 ? 'all' : 'trash')
    setSearchQuery('')
    if (searchRef.current) searchRef.current.value = ''
    tableRef.current?.clearPage()
  }

  const handleSearch = () => {
    const q = searchRef.current?.value || ''
    setSearchQuery(q)
    tableRef.current?.applyFilter('search', q)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch()
  }

  const handleOpenDetail = useCallback((row: Record<string, unknown>) => {
    const id = row.id
    if (typeof id === 'number' && id > 0) {
      setViewRowId(id)
      return
    }
    if (typeof id === 'string' && id.trim() !== '') {
      const n = Number(id)
      if (Number.isFinite(n) && n > 0) setViewRowId(n)
    }
  }, [])

  const handleCloseDetail = useCallback(() => {
    setViewRowId(null)
    setViewRow(null)
  }, [])

  const handleCloseForm = useCallback(() => {
    onClose()
    setEditingId(null)
    setDuplicateFrom(null)
  }, [onClose])

  const handleEditFromDetail = useCallback(
    (row: TicketRequest) => {
      handleCloseDetail()
      setDuplicateFrom(null)
      setEditingId(row.id ?? null)
      onOpen()
    },
    [handleCloseDetail, onOpen]
  )

  const handleDuplicateFromDetail = useCallback(
    (row: TicketRequest) => {
      handleCloseDetail()
      setEditingId(null)
      const base = ticketRequestToInitialData(row)
      setDuplicateFrom({
        ...base,
        id: undefined,
        attachment_metadata: [],
      })
      onOpen()
    },
    [handleCloseDetail, onOpen]
  )

  const handleCreateNew = useCallback(() => {
    setEditingId(null)
    setDuplicateFrom(null)
    onOpen()
  }, [onOpen])

  const handleOpenCallOut = useCallback(
    (row?: Record<string, unknown>) => {
      if (!hasValidProfilePhone(user)) {
        notifyMissingProfilePhone(showError)
        return
      }
      setCallOutPayload(buildCallOutPayloadFromRow(user, row))
      onCallOutOpen()
    },
    [onCallOutOpen, showError, user]
  )

  const tableOptions: DataTableOptions = useMemo(
    () => ({
      selectable: false,
      dataFields: {
        request_number: { name: 'Request #', withSort: true, maxWidth: '280px' },
        parent_ticket_number: { name: 'Ticket', maxWidth: '160px' },
        service_type: { name: 'Service', maxWidth: '160px' },
        description: { name: 'Issue', maxWidth: '340px' },
        ticket_status: {
          name: 'Status',
          withFilter: true,
          filterOptions: [
            { value: '1', label: 'New' },
            { value: '2', label: 'Pending' },
            { value: '3', label: 'In Progress' },
            { value: '4', label: 'Resolved' },
            { value: '5', label: 'Closed' },
            { value: '6', label: 'Cancelled' },
            { value: '0', label: 'Draft' },
          ],
          badge: {
            '0': 'gray',
            '1': 'blue',
            '2': 'yellow',
            '3': 'orange',
            '4': 'green',
            '5': 'gray',
            '6': 'red',
          },
          badgeLabels: {
            '0': 'Draft',
            '1': 'New',
            '2': 'Pending',
            '3': 'In Progress',
            '4': 'Resolved',
            '5': 'Closed',
            '6': 'Cancelled',
          },
          maxWidth: '140px',
        },
        created_at: { name: 'Date Created', withSort: true, maxWidth: '180px' },
        last_updated_at_human: { name: 'Last Updated', maxWidth: '120px' },
      },
      dataSource: activeTab === 'trash' ? '/support/my-request/archived' : '/support/my-request',
      primaryKey: 'id',
      softDelete: true,
      displayInModal: true,
      noRecordText: 'No requests found',
      canEditRow: (row) => !row.submitted_at,
    }),
    [activeTab]
  )

  const customRenderers = useMemo(
    () => ({
      request_number: (value: string | null | undefined) => {
        const num = value ?? ''
        if (!num) return ''
        return (
          <Text as="span" whiteSpace="nowrap" fontFamily="mono" fontSize="sm">
            {num}
          </Text>
        )
      },
      parent_ticket_number: (value: string | null | undefined) => {
        const num = value ?? ''
        if (!num) return '–'
        return (
          <Text as="span" whiteSpace="nowrap" fontFamily="mono" fontSize="sm" color="blue.600">
            {num}
          </Text>
        )
      },
      description: (value: unknown, row?: Record<string, unknown>) => {
        const text = value ? String(value) : '–'
        if (!row) {
          return <Text as="span">{text}</Text>
        }
        return (
          <Text
            as="button"
            type="button"
            variant="link"
            color={colors.linkHover}
            fontWeight="500"
            textAlign="left"
            textDecoration="underline"
            cursor="pointer"
            whiteSpace="normal"
            noOfLines={3}
            title={text !== '–' ? text : undefined}
            onClick={(e) => {
              e.stopPropagation()
              handleOpenDetail(row)
            }}
          >
            {text}
          </Text>
        )
      },
      created_at: (value: string | null | undefined) => {
        if (!value) return ''
        try {
          const date = typeof value === 'string' ? parseISO(value) : new Date(value)
          return format(date, 'MMM d, yyyy · h:mm a')
        } catch {
          return String(value)
        }
      },
    }),
    [colors.linkHover, handleOpenDetail]
  )

  return (
    <AdminLayout>
      <Box p={4} h="100%" overflow="hidden" display="flex" flexDirection="column">
        <VStack spacing={2} align="stretch" flex="1" overflow="hidden" minH={0}>
          <Card flexShrink={0} display="flex" flexDirection="column" flex="1" minH={0} overflow="hidden">
            <CardHeader flexShrink={0} py={3}>
              <Heading size="md" mb={1}>
                My Requests
              </Heading>
              <Text fontSize="sm" color={colors.text}>
                View and manage your support requests
              </Text>
            </CardHeader>

            <CardHeader py={0} px={0} flexShrink={0}>
              <Box pt={2} pb={0}>
                <HStack justify="space-between" align="flex-end" spacing={4} px={4} width="100%">
                  <Tabs index={activeTab === 'all' ? 0 : 1} onChange={handleTabChange}>
                    <TabList borderBottom="none">
                      <Tab>
                        All{' '}
                        <Badge ml={2} colorScheme="blue">
                          {totalRequests}
                        </Badge>
                      </Tab>
                      <Tab>
                        Trash{' '}
                        <Badge ml={2} colorScheme="red">
                          {totalTrash}
                        </Badge>
                      </Tab>
                    </TabList>
                  </Tabs>
                  <HStack pb={2} spacing={2} align="flex-end">
                    <ActionButtons createLabel="Create Request" onCreate={handleCreateNew} pathname="/my-request" />
                    <VStack spacing={0} align="flex-end">
                      <Button
                        leftIcon={<PhoneCall size={18} />}
                        colorScheme="green"
                        size="md"
                        onClick={() => handleOpenCallOut()}
                      >
                        Call out
                      </Button>
                      <Text fontSize="xs" color="gray.500" mt={1} textAlign="right" maxW="220px">
                        ALICE will call your profile number to complete intake
                      </Text>
                    </VStack>
                  </HStack>
                </HStack>
                <Box borderBottom="2px" borderColor={colors.border} mx={4} />
              </Box>
            </CardHeader>

            <CardHeader py={1} pt={3} pb={2} flexShrink={0}>
              <HStack spacing={4} flexWrap="wrap">
                <Box flex="0 1 auto" minW="250px" maxW="400px">
                  <InputGroup size="sm">
                    <Input
                      ref={searchRef}
                      placeholder="Search by request number, description..."
                      onKeyDown={handleKeyDown}
                      defaultValue={searchQuery}
                      pr="2.5rem"
                    />
                    <InputRightElement width="2.5rem">
                      <IconButton
                        aria-label="Search"
                        icon={<Search size={16} />}
                        size="sm"
                        h="1.75rem"
                        w="1.75rem"
                        onClick={handleSearch}
                        variant="ghost"
                      />
                    </InputRightElement>
                  </InputGroup>
                </Box>
              </HStack>
            </CardHeader>

            <CardBody
              p={0}
              pt={1}
              pb={4}
              mx={5}
              flex="1"
              overflow="hidden"
              display="flex"
              flexDirection="column"
              minH={0}
              position="relative"
            >
              <Box h="100%" display="flex" flexDirection="column" minH={0}>
                <DataTable
                  ref={tableRef}
                  options={tableOptions}
                  customRenderers={customRenderers}
                  access={{
                    can_create: access.can_create,
                    can_edit: access.can_edit,
                    can_delete: access.can_delete,
                    can_view: access.can_view,
                    can_export: access.can_export,
                    can_import: access.can_import,
                  }}
                  onOpenModal={(id: number) => {
                    setDuplicateFrom(null)
                    setEditingId(id)
                    onOpen()
                  }}
                  setSubSub={showSubSubCallback}
                  params={tableParams}
                />
              </Box>
            </CardBody>
          </Card>
        </VStack>
      </Box>

      <CreateTicketModal
        isOpen={isOpen}
        onClose={handleCloseForm}
        editingId={editingId}
        duplicateFrom={duplicateFrom}
        defaultContact={editingId == null ? defaultContact : undefined}
        profilePhoneMissing={!hasValidProfilePhone(user)}
        onSuccess={() => tableRef.current?.reload()}
      />

      <TicketRequestDetailModal
        isOpen={viewRowId != null}
        onClose={handleCloseDetail}
        row={viewRow}
        isLoading={isLoadingViewRow}
        showCallOut={activeTab !== 'trash'}
        onCallOut={handleOpenCallOut}
        onEdit={activeTab !== 'trash' ? handleEditFromDetail : undefined}
        onDuplicate={activeTab !== 'trash' ? handleDuplicateFromDetail : undefined}
      />

      <AliceCallOutConfirmModal
        isOpen={isCallOutOpen}
        onClose={() => {
          onCallOutClose()
          setCallOutPayload(null)
        }}
        payload={callOutPayload}
        onSubmitted={() => {
          tableRef.current?.reload()
        }}
      />
    </AdminLayout>
  )
}
